import '../base';
import './pdp-upsell';

import { ProductForm, SEL_FORM_CONTAINER } from "../objects/product/product-form";
import { EventVariantChanged, EVT_VARIANT_CHANGED } from '../objects/product/variant-selector';
import { StickyAddToCart } from "../objects/product/sticky-add-to-cart";
import { Section } from "../tools/section";
import { Accordion } from "../objects/Accordion";
import { ProductGalleryClientTags } from "../objects/product-gallery-client-tags";
import { ProductGalleryLightbox } from "../objects/product-gallery-lightbox";
import { ProductGalleryNav } from "../objects/product-gallery-nav";
import { CLASS_DISABLED } from '../tools/constants/classes';
import { rebindWishlist } from '../objects/swym-wishlist';
import { rebindContentDrawerEvents } from '../objects/content-drawer';

// Import RecentlyViewed for product tracking
declare global {
  interface Window {
    RecentlyViewed: any;
  }
}


const SEL_TEMPLATE_CONTAINER = '[data-product-main]';
const SELECTOR_ACCORDION = '[data-product-accordion]';
const SELECTOR_READ_MORE_BUTTON = '[data-read-more-button]';
const SELECTOR_READ_LESS_BUTTON = '[data-read-less-button]';
const SELECTOR_STICKY_ATC = '[data-sticky-atc]';
const SELECTOR_VARIANT_SELECTOR_DATA = '[data-variant-selector-data]';
const SELECTOR_WISHLIST_BUTTON =    '[data-wishlist-button]';

class ProductMain extends Section {
  private formElement:HTMLFormElement|null = null;
  private form:ProductForm|null = null;
  private accordionElement:HTMLElement|null = null;
  private accordion:Accordion|null = null;
  private readMoreButton:HTMLButtonElement|null = null;
  private readLessButton:HTMLButtonElement|null = null;
  private stickyAtcElement:HTMLElement|null = null;
  private stickyAtc:StickyAddToCart|null = null;
  private galleryElement:HTMLElement|null = null;
  private galleryClientTags:ProductGalleryClientTags|null = null;
  private galleryLightbox:ProductGalleryLightbox|null = null;
  private galleryNav:ProductGalleryNav|null = null;

  queryChildren(container: HTMLElement): void {
    this.formElement = container.querySelector(SEL_FORM_CONTAINER);
    this.accordionElement = container.querySelector(SELECTOR_ACCORDION);
    this.readMoreButton= container.querySelector(SELECTOR_READ_MORE_BUTTON);
    this.readLessButton= container.querySelector(SELECTOR_READ_LESS_BUTTON);
    this.stickyAtcElement = container.querySelector(SELECTOR_STICKY_ATC);
    this.galleryElement = container.querySelector('[data-product-gallery]');
  }

  attachEvents(container: HTMLElement): void {
    if(this.formElement) {
      this.form = new ProductForm(this.formElement);
      //Example of how to listen to a variant change on the form and access
      //that data in a separate Class.
      this.formElement.addEventListener(EVT_VARIANT_CHANGED, this.onVariantChange);
    }

    // Initialize sticky add to cart
    if(this.stickyAtcElement) {
      this.stickyAtc = new StickyAddToCart(this.stickyAtcElement);
    }

    // New accordion instance
    if(this.accordionElement) this.accordion = new Accordion(this.accordionElement);

    // Initialize content drawer events
    rebindContentDrawerEvents();
    // Initialize gallery client tags
    if(this.galleryElement) {
      this.galleryClientTags = new ProductGalleryClientTags(this.galleryElement);
      if (typeof window.Currency !== 'undefined' && window.Currency.currency === 'USD') {
        this.galleryClientTags.convertUnits(true);
      }
    }

    // Initialize gallery lightbox
    if(this.galleryElement) {
      this.galleryLightbox = new ProductGalleryLightbox(this.galleryElement);
    }

    // Initialize gallery nav (scroll progress tracker)
    if(this.galleryElement) {
      this.galleryNav = new ProductGalleryNav(this.galleryElement);
    }


    this.readMoreButton?.addEventListener('click', (e)=> {
      e.preventDefault();
      // Open product info drawer with Product Details tab selected
      const drawer = document.querySelector('[data-content-drawer="product-info"]') as HTMLElement;
      if (drawer) {
        // Import the openContentDrawer function
        import('../objects/content-drawer').then(({ openContentDrawer }) => {
          openContentDrawer(drawer, 'product-details');
        });
      }
    })

    this.readLessButton?.addEventListener('click', (e)=> {
      e.preventDefault();
      // Close product info drawer
      const drawer = document.querySelector('[data-content-drawer="product-info"]') as HTMLElement;
      if (drawer) {
        // Import the closeContentDrawer function
        import('../objects/content-drawer').then(({ closeContentDrawer }) => {
          closeContentDrawer(drawer);
        });
      }
    })

    // Track this product in recently viewed
    this.trackRecentlyViewedProduct(container);

    // Initialize wishlist functionality
    rebindWishlist();
  }

  detatchEvents() {
    if(this.accordion) this.accordion.detachEvents();
    if(this.form) this.form.detatchEvents();
    if(this.stickyAtc) this.stickyAtc.dispose();
    if(this.galleryClientTags) this.galleryClientTags.destroy();
    if(this.galleryLightbox) this.galleryLightbox.destroy();
    if(this.galleryNav) this.galleryNav.destroy();
    if(this.formElement) {
      this.formElement.removeEventListener(EVT_VARIANT_CHANGED, this.onVariantChange);
    }
  }

  onVariantChange = (e:Event) => {
    //Get the variant details out of the dispatched that we listen to
    const { detail } = e as EventVariantChanged;
    const { oldVariant, newVariant } = detail;

    //Here you could add in code to change image gallery slides or update 
    //elements based on the new variant.

  }

  private trackRecentlyViewedProduct(container: HTMLElement): void {
    try {
      // Get the product data from the JSON script tag
      const productDataElement = container.querySelector(SELECTOR_VARIANT_SELECTOR_DATA) as HTMLScriptElement;
      
      if (productDataElement) {
        const productData = JSON.parse(productDataElement.innerHTML);
        const productId = productData.id;
        const productHandle = productData.handle;

        // Get product metafields from the JSON script tag
        let productMetafieldsData = null;
        try {
          const metafieldsScript = document.querySelector('[data-product-metafields]');
          if (metafieldsScript && metafieldsScript.textContent) {
            productMetafieldsData = JSON.parse(metafieldsScript.textContent);
          }
        } catch (e) {
          // Failed to parse product metafields JSON
        }

        // Store metafield data in sessionStorage for Recently Viewed to access
        if (productId && productHandle && productMetafieldsData) {
          const metafieldsData = {
            colour: (productMetafieldsData as any).metafields?.colour || null,
            usp_tags: (productMetafieldsData as any).metafields?.usp_tags || null
          };

          // Store in sessionStorage with product ID as key
          sessionStorage.setItem(`product_metafields_${productId}`, JSON.stringify(metafieldsData));
        }

        if (productId && productHandle) {
          const addProductWithRetry = (retries: number = 3) => {
            if (window.RecentlyViewed) {
              // Add the product to recently viewed with both ID and handle
              window.RecentlyViewed.addProduct({ id: productId, handle: productHandle });
            } else if (retries > 0) {
              setTimeout(() => addProductWithRetry(retries - 1), 100);
            } else {
              // Fallback: directly set cookie
              this.addProductDirectly(productId, productHandle);
            }
          };
          
          addProductWithRetry();
        }
      }
    } catch (error) {
      // Error tracking recently viewed product
    }
  }

  private addProductDirectly(productId: number, productHandle: string): void {
    try {
      const COOKIE_NAME = 'recently_viewed_products';
      const MAX_PRODUCTS = 10;
      
      // Get existing products
      const cookieValue = this.getCookieValue(COOKIE_NAME);
      let products: Array<{id: number, handle: string}> = [];
      
      if (cookieValue) {
        try {
          const parsed = JSON.parse(decodeURIComponent(cookieValue));
          if (Array.isArray(parsed)) {
            products = parsed.filter(p => typeof p === 'object' && p.id && p.handle);
          }
        } catch (e) {
          // Failed to parse existing cookie
        }
      }
      
      // Remove existing product and add new one at the beginning
      products = products.filter(p => p.id !== productId);
      products.unshift({ id: productId, handle: productHandle });
      products = products.slice(0, MAX_PRODUCTS);
      
      // Set cookie
      const expires = new Date();
      expires.setTime(expires.getTime() + 30 * 24 * 60 * 60 * 1000); // 30 days
      const cookieString = `${COOKIE_NAME}=${encodeURIComponent(JSON.stringify(products))};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
      document.cookie = cookieString;
      
    } catch (error) {
      // Direct cookie fallback failed
    }
  }

  private getCookieValue(name: string): string | null {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop()?.split(';').shift() || null;
    }
    return null;
  }
}

Section.register(SEL_TEMPLATE_CONTAINER, ProductMain);